'use client';

import { useEffect, useState } from 'react';
import Script from 'next/script';
import { GA_ID } from '@/lib/analytics';
import { Button } from '@/components/ui/button';

const CONSENT_KEY = 'cookie_consent';

type Consent = 'granted' | 'denied' | null;

function getStoredConsent(): Consent {
  if (typeof window === 'undefined') return null;
  const v = localStorage.getItem(CONSENT_KEY);
  return v === 'granted' || v === 'denied' ? v : null;
}

export function CookieConsent() {
  const [consent, setConsent] = useState<Consent>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!GA_ID) return;
    const stored = getStoredConsent();
    setConsent(stored);
    if (!stored) setVisible(true);
  }, []);

  const handleChoice = (choice: 'granted' | 'denied') => {
    localStorage.setItem(CONSENT_KEY, choice);
    setConsent(choice);
    setVisible(false);

    if (choice === 'granted' && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
      });
    }
  };

  if (!GA_ID) return null;

  return (
    <>
      {/* GA scripts — always load but default to denied until consent */}
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}
        strategy="afterInteractive"
      />
      <Script id="ga-init" strategy="afterInteractive">
        {`
          window.dataLayer=window.dataLayer||[];
          function gtag(){dataLayer.push(arguments)}
          gtag('consent','default',{analytics_storage:'${consent === 'granted' ? 'granted' : 'denied'}'});
          gtag('js',new Date());
          gtag('config','${GA_ID}');
        `}
      </Script>

      {/* Banner */}
      {visible && (
        <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 bg-background/95 backdrop-blur border-t shadow-lg">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center gap-4">
            <p className="text-sm text-muted-foreground flex-1">
              We use cookies to analyze how the platform is used and improve your experience.
              No advertising or third-party tracking.
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleChoice('denied')}
              >
                Decline
              </Button>
              <Button
                size="sm"
                onClick={() => handleChoice('granted')}
              >
                Accept
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
